import React, { useState } from 'react';
import { Layers, Plus } from 'lucide-react';

// Expands "87-92" / "87,89,91-93" / "A1,A2" into individual unit tokens
export const parseUnitNumbers = (input) => {
  const out = [];
  for (const raw of String(input || '').split(',')) {
    const token = raw.trim();
    if (!token) continue;
    const m = token.match(/^(\d+)\s*-\s*(\d+)$/);
    if (m) {
      const a = parseInt(m[1], 10);
      const b = parseInt(m[2], 10);
      if (b >= a && b - a < 100) {
        for (let n = a; n <= b; n++) out.push(String(n));
        continue;
      }
    }
    out.push(token);
  }
  return out.slice(0, 100);
};

// Quick generator for identical-price multi-unit jobs (e.g. site plots).
// The SERVICE lives on the job's main service field (picked once at the top
// of the modal) — lines here are just "Plot 87" etc so the invoice shows the
// services once as a header, then each plot with its price.
export const BulkUnitAdder = ({ onAdd }) => {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('Plot');
  const [numbers, setNumbers] = useState('');
  const [priceEach, setPriceEach] = useState('');

  const units = parseUnitNumbers(numbers);
  const each = parseFloat(priceEach) || 0;
  const canAdd = units.length > 0 && each > 0;

  const handleAdd = () => {
    const lines = units.map((u) => ({
      service: `${label.trim() ? label.trim() + ' ' : ''}${u}`,
      rate: each,
      quantity: 1,
    }));
    onAdd(lines);
    setNumbers('');
    setOpen(false);
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full py-2 border-2 border-dashed border-indigo-300 rounded-xl text-sm font-medium text-indigo-600 hover:border-indigo-400 hover:bg-indigo-50 transition-colors flex items-center justify-center gap-1.5"
        data-testid="bulk-unit-adder-toggle"
      >
        <Layers size={15} />
        Bulk add plots / units…
      </button>
    );
  }

  return (
    <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 space-y-2" data-testid="bulk-unit-adder">
      <div className="flex items-center gap-1.5">
        <Layers size={14} className="text-indigo-600" />
        <span className="text-[11px] font-semibold text-indigo-800 uppercase tracking-wide">
          Bulk add plots / units
        </span>
      </div>

      <div>
        <label className="block text-[11px] text-zinc-500 mb-1">Uses the services picked above — just add the units</label>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="block text-[11px] text-zinc-500 mb-1">Label</label>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-zinc-300 text-sm focus:border-indigo-500 outline-none bg-white"
            data-testid="bulk-unit-label-input"
          />
        </div>
        <div>
          <label className="block text-[11px] text-zinc-500 mb-1">Numbers</label>
          <input
            type="text"
            value={numbers}
            onChange={(e) => setNumbers(e.target.value)}
            placeholder="87-92"
            className="w-full px-3 py-2 rounded-lg border border-zinc-300 text-sm focus:border-indigo-500 outline-none bg-white"
            data-testid="bulk-unit-numbers-input"
          />
        </div>
        <div>
          <label className="block text-[11px] text-zinc-500 mb-1">£ each</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={priceEach}
            onChange={(e) => setPriceEach(e.target.value)}
            placeholder="40"
            className="w-full px-3 py-2 rounded-lg border border-zinc-300 text-sm focus:border-indigo-500 outline-none bg-white"
            data-testid="bulk-unit-price-input"
          />
        </div>
      </div>

      <p className="text-[11px] text-zinc-500">
        Ranges and lists work: <span className="font-mono">87-92</span> or <span className="font-mono">87,89,91-93</span>
        {canAdd && (
          <span className="text-indigo-700 font-semibold"> — {units.length} line{units.length > 1 ? 's' : ''} × £{each.toFixed(2)} = £{(units.length * each).toFixed(2)}</span>
        )}
      </p>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="flex-1 py-2 bg-white border border-zinc-200 rounded-lg text-sm font-medium text-zinc-600 hover:bg-zinc-50 transition-colors"
          data-testid="bulk-unit-cancel-btn"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleAdd}
          disabled={!canAdd}
          className="flex-1 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1"
          data-testid="bulk-unit-add-btn"
        >
          <Plus size={15} />
          Add {units.length > 0 ? `${units.length} line${units.length > 1 ? 's' : ''}` : 'lines'}
        </button>
      </div>
    </div>
  );
};

export default BulkUnitAdder;
